/**
 * Prototype-only: the revenue statements the import writes into, and the
 * catalogue the failing one points at.
 *
 * A statement created by a failing import spends a few seconds Processing and
 * then lands on Failed, carrying the backend's sentence. A re-import puts it
 * back through the same few seconds and reads the dev bar's "Catalog fixed"
 * dial to decide where it comes out.
 */
import { jsonResponse, paginated, registerDevMockRoutes } from "@/lib/dev-mode";
import type { RevenueStatement } from "@/types";
import {
  catalogRecordings,
  catalogReleases,
  makeStatement,
  REVENUE_ERROR,
  SEEDED_STATEMENTS,
  STATEMENT_CONTRIBUTORS,
  STATEMENT_TRACKS,
} from "./revenueFixtures";
import { isCatalogFixed } from "./scenario";

const STATEMENTS_KEY = "rie-statements";
const PHASE_KEY = "rie-phase-ms";

/** How long a statement stays in Processing. The smoke test shortens it. */
function processingMs(): number {
  const override = Number(localStorage.getItem(PHASE_KEY));
  return override > 0 ? override : 3000;
}

/** A statement plus the bookkeeping the mock needs and the API would not send. */
interface StoredStatement extends RevenueStatement {
  phase_start: number;
}

function load(): StoredStatement[] {
  try {
    const raw = localStorage.getItem(STATEMENTS_KEY);
    if (raw) return JSON.parse(raw) as StoredStatement[];
  } catch {
    /* fall through to the seed */
  }
  return SEEDED_STATEMENTS.map((statement) => ({ ...statement, phase_start: 0 }));
}

function save(statements: StoredStatement[]): void {
  try {
    localStorage.setItem(STATEMENTS_KEY, JSON.stringify(statements));
  } catch {
    /* the run still works for this page view */
  }
}

export function resetRevenueMock(): void {
  try {
    localStorage.removeItem(STATEMENTS_KEY);
  } catch {
    /* nothing to clear */
  }
}

function onTheWire(statement: StoredStatement): RevenueStatement {
  const { phase_start: _phaseStart, ...rest } = statement;
  return rest;
}

/** Where a Processing statement comes out, once its few seconds are up. */
function settleOutcome(statement: StoredStatement): StoredStatement {
  if (isCatalogFixed()) {
    return {
      ...statement,
      status: "processed",
      import_error: "",
      revenue: "12480.55",
      expected_amount: "12480.55",
      updated_at: new Date().toISOString(),
    };
  }
  return {
    ...statement,
    status: "failed",
    import_error: REVENUE_ERROR,
    revenue: "0.00",
    expected_amount: null,
    updated_at: new Date().toISOString(),
  };
}

/** Advance anything that has been Processing long enough. */
function advance(statements: StoredStatement[]): StoredStatement[] {
  let changed = false;
  const next = statements.map((statement) => {
    if (statement.status !== "generating") return statement;
    if (Date.now() - statement.phase_start < processingMs()) return statement;
    changed = true;
    return settleOutcome(statement);
  });
  if (changed) save(next);
  return next;
}

/**
 * Called when the wizard confirms an import: the statement the file produced.
 * It starts Processing; where it lands is the "Catalog fixed" dial's call, the
 * same for a first run as for a re-import — the dial stands for whether the
 * catalogue has the tracks the revenue needs.
 */
export function createImportedStatement(
  batchId: string,
  fileName: string | null,
): RevenueStatement {
  const statement: StoredStatement = {
    ...makeStatement({
      import_batch: batchId,
      file_name: fileName ?? "spotify_q1_2026.csv",
      status: "generating",
      revenue: "0.00",
      expected_amount: null,
    }),
    phase_start: Date.now(),
  };
  save([statement, ...load()]);
  return onTheWire(statement);
}

/**
 * Put a failed statement in the list without running the wizard first — the
 * way the smoke test reaches the list and details screens.
 */
export function seedFailedStatement(): RevenueStatement {
  const statement: StoredStatement = {
    ...makeStatement({
      id: "stmt-failed",
      status: "failed",
      revenue: "0.00",
      expected_amount: null,
      import_error: REVENUE_ERROR,
    }),
    phase_start: 0,
  };
  save([statement, ...load()]);
  return onTheWire(statement);
}

registerDevMockRoutes((request, url) => {
  const path = url.pathname.replace(/\/$/, "");
  const { method } = request;

  if (path.endsWith("/revenue/statements") && method === "GET") {
    const statements = advance(load());
    return jsonResponse(paginated(statements.map(onTheWire)));
  }

  const match = path.match(/\/revenue\/statements\/([^/]+)(\/[^/]+)?$/);
  if (match) {
    const [, id, sub] = match;
    const statements = advance(load());
    const index = statements.findIndex((statement) => statement.id === id);
    if (index === -1) return jsonResponse({ detail: "Not found." }, 404);
    const statement = statements[index];

    if (!sub && method === "GET") return jsonResponse(onTheWire(statement));

    if (sub === "/reimport" && method === "POST") {
      statements[index] = {
        ...statement,
        status: "generating",
        import_error: "",
        phase_start: Date.now(),
        updated_at: new Date().toISOString(),
      };
      save(statements);
      return jsonResponse(onTheWire(statements[index]));
    }

    // The details tabs only render for a statement that imported, so the same
    // rows serve every one of them.
    if (sub === "/top-tracks" || sub === "/track-revenue") {
      return jsonResponse(paginated(STATEMENT_TRACKS));
    }
    if (sub === "/contributors-revenue") {
      return jsonResponse(paginated(STATEMENT_CONTRIBUTORS));
    }

    if (!sub && method === "DELETE") {
      statements.splice(index, 1);
      save(statements);
      return jsonResponse(null, 204);
    }
  }

  // The catalogue "Go to Catalog" lands on, and the tracks the dial adds.
  if (path.endsWith("/catalogue/releases") && method === "GET") {
    return jsonResponse(paginated(catalogReleases(isCatalogFixed())));
  }
  if (path.endsWith("/catalogue/recordings") && method === "GET") {
    return jsonResponse(paginated(catalogRecordings(isCatalogFixed())));
  }

  return null;
});
